import { STATE_UPDATE_TIMEOUT } from "../../test/constants/numbers"
import { IPing } from "../interfaces/measurement-result.interface"
import { ICurveItem, IPingItem } from "../interfaces/open-test-response"
import { IOverallResult } from "../interfaces/overall-result.interface"

const noTransferThreshold = 1400

export class CalcService {
  private static instance = new CalcService()

  static get I() {
    return this.instance
  }

  private constructor() {}

  getOverallPings(pings: IPingItem[] | undefined): IPing[] {
    if (!pings?.length) {
      return []
    }
    let startX = 0
    return pings
      .map((p) => ({
        time_ns: p.time_elapsed * 1e6,
        value: p.ping_ms * 1e6,
        value_server: p.ping_ms * 1e6,
      }))
      .filter((p) => p.time_ns > 0)
      .reduce((acc, p, i) => {
        if (i === 0) {
          startX = p.time_ns
        }
        return [...acc, { ...p, time_ns: p.time_ns - startX }]
      }, [] as IPing[])
  }

  getOverallResultsFromSpeedCurve(
    curve: ICurveItem[] | undefined,
    stepMs = STATE_UPDATE_TIMEOUT,
  ): IOverallResult[] {
    if (!curve?.length) {
      return []
    }
    if (!stepMs) {
      return curve.map(this.parseCurveItem)
    }
    const options = {
      lastBytes: 0,
      lastMs: 0,
      stepMs,
    }
    const resp: IOverallResult[] = [
      {
        bytes: 0,
        nsec: 0,
        speed: 0,
      },
    ]
    for (const ci of curve) {
      const result = this.calcResultForStep(ci, options)
      if (result) {
        const prevResult = resp[resp.length - 1]
        if (
          prevResult &&
          result.nsec - prevResult.nsec > noTransferThreshold * 1e6
        ) {
          // fill gap with zero-speed entries
          resp.push({
            ...prevResult,
            speed: 0,
          })
          resp.push({
            ...result,
            speed: 0,
          })
        }
        resp.push(result)
      }
    }
    return resp
  }

  private calcResultForStep(
    ci: ICurveItem,
    options: {
      lastBytes: number
      lastMs: number
      stepMs: number
    },
  ) {
    const { lastBytes, lastMs, stepMs } = options
    let retVal: IOverallResult | null = null
    if (ci.time_elapsed - lastMs >= stepMs) {
      retVal = this.parseCurveItem({
        bytes_total: ci.bytes_total,
        time_elapsed: ci.time_elapsed,
        speed: this.calcSpeed(
          ci.bytes_total - lastBytes,
          ci.time_elapsed - lastMs,
        ),
      })
      options.lastBytes = ci.bytes_total
      options.lastMs = ci.time_elapsed
    }
    return retVal
  }

  private calcSpeed(bytes: number, timeMs: number) {
    return Math.max((bytes * 8) / (timeMs / 1e3), 0)
  }

  private parseCurveItem = (ci: ICurveItem) => ({
    bytes: ci.bytes_total,
    nsec: ci.time_elapsed * 1e6,
    speed: ci.speed ?? this.calcSpeed(ci.bytes_total, ci.time_elapsed),
  })
}
